import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarCheck,
  CalendarDays,
  ClipboardList,
  FileText,
  FlaskConical,
  HeartPulse,
  Home,
  List,
  MoreHorizontal,
  PanelLeftClose,
  Pill,
  Scissors,
} from "lucide-react";
import { ChartSubtabs } from "@/components/patient/ChartFolderNav";
import EncountersWorkspace from "@/components/patient/EncountersWorkspace";
import AppointmentsWorkspace from "@/components/patient/AppointmentsWorkspace";
import MedicalHistoryWorkspace from "@/components/patient/MedicalHistoryWorkspace";
import AllergiesWorkspace from "@/components/patient/AllergiesWorkspace";
import SurgicalHistoryWorkspace from "@/components/patient/SurgicalHistoryWorkspace";
import NotesWorkspace from "@/components/patient/NotesWorkspace";
import ProblemListWorkspace from "@/components/patient/ProblemListWorkspace";
import WhatToOrderPanel from "@/components/encounter/WhatToOrderPanel";
import { ClinicalSummary } from "@/components/patient/ClinicalSummary";
import { ResultsPanel } from "@/pages/PatientChartPage";
import { ENC } from "@/constants/testIds";
import { canAccessMainTab, canWriteMainTab } from "@/lib/maintabAccess";
import { CLINICAL_TABS } from "@/lib/patientChartClinical";
import {
  canAccessSubtab,
  canWriteSubtab,
  cycleVisibleTab,
  filterTabsByAccess,
  firstAllowedTab,
} from "@/lib/subtabAccess";
import { fetchPatientEncounters, sortEncountersNewest } from "@/lib/patientChart";
import { encounterScreenPath } from "@/lib/patientChartNewEncounter";
import {
  loadSummaryAllergies,
  loadSummaryMedicalHistory,
  loadSummaryProblemList,
  loadSummarySurgicalHistory,
} from "@/lib/patientSummary";

const CHART_ICONS = {
  summary: Home,
  "problem-list": List,
  "medical-history": HeartPulse,
  "surgical-history": Scissors,
  encounters: CalendarDays,
  appointments: CalendarCheck,
  notes: FileText,
  results: FlaskConical,
  medications: Pill,
  allergies: AlertTriangle,
  miscellaneous: MoreHorizontal,
  orders: ClipboardList,
};

const ORDERS_TAB = { id: "orders", label: "Orders", color: "#93CABF" };

function Card({ title, children, testId }) {
  return (
    <section data-testid={testId} className="card emr-encdetail__section">
      {title ? <h2 className="emr-encdetail__section-title">{title}</h2> : null}
      {children}
    </section>
  );
}

/**
 * The Patient Chart embedded in the left column of the Encounter screen.
 * Self-contained: it fetches its own summary/encounters data independent of
 * the Encounter/Note state so switching tabs here never touches, remounts,
 * or reloads the Note editor on the right.
 */
export default function EncounterPatientChart({ patientId, fields, currentUser, onCollapse }) {
  const navigate = useNavigate();
  const subtabAccess = currentUser?.subtabAccess;
  const canWriteEncounters = canWriteMainTab(currentUser?.maintabAccess, "encounters");
  const canReadEncounters = canAccessMainTab(currentUser?.maintabAccess, "encounters");
  const canWriteAppointmentsMain = canWriteMainTab(currentUser?.maintabAccess, "appointments");
  const canWriteNotes = canWriteSubtab(subtabAccess, "clinical", "notes");
  const canWriteProblems = canWriteSubtab(subtabAccess, "clinical", "problem-list");
  const canWritePmh = canWriteSubtab(subtabAccess, "clinical", "medical-history");
  const canWriteSurgHx = canWriteSubtab(subtabAccess, "clinical", "surgical-history");
  const canWriteAllergies = canWriteSubtab(subtabAccess, "clinical", "allergies");
  const canWriteClinAppointments = canWriteAppointmentsMain && canWriteSubtab(subtabAccess, "clinical", "appointments");
  const canReadProblems = canAccessSubtab(subtabAccess, "clinical", "problem-list");
  const canReadPmh = canAccessSubtab(subtabAccess, "clinical", "medical-history");
  const canReadSurgHx = canAccessSubtab(subtabAccess, "clinical", "surgical-history");
  const canReadAllergies = canAccessSubtab(subtabAccess, "clinical", "allergies");
  const canReadClinicalEncounters = canAccessSubtab(subtabAccess, "clinical", "encounters");
  const canReadResults = canAccessSubtab(subtabAccess, "clinical", "results");

  const clinicalTabs = useMemo(
    () => filterTabsByAccess(CLINICAL_TABS, "clinical", subtabAccess),
    [subtabAccess],
  );
  const chartTabs = useMemo(() => [...clinicalTabs, ORDERS_TAB], [clinicalTabs]);

  const [activeTab, setActiveTab] = useState(() =>
    firstAllowedTab(CLINICAL_TABS, "clinical", subtabAccess, "summary") || "summary",
  );
  const [encounters, setEncounters] = useState([]);
  const [problemList, setProblemList] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState({ conditions: [], negatives: [] });
  const [surgicalHistory, setSurgicalHistory] = useState({ items: [] });
  const [allergies, setAllergies] = useState({ items: [], noKnownAllergies: false });

  useEffect(() => {
    setActiveTab((current) => firstAllowedTab(CLINICAL_TABS, "clinical", subtabAccess, current));
  }, [subtabAccess]);

  const loadEncounters = useCallback(async () => {
    if (!patientId || !canReadEncounters) {
      setEncounters([]);
      return;
    }
    try {
      const data = await fetchPatientEncounters(patientId);
      setEncounters(sortEncountersNewest(data?.items || []));
    } catch {
      setEncounters([]);
    }
  }, [patientId, canReadEncounters]);

  useEffect(() => {
    loadEncounters();
  }, [loadEncounters]);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    if (canReadProblems) {
      loadSummaryProblemList(patientId)
        .then((items) => !cancelled && setProblemList(items))
        .catch(() => !cancelled && setProblemList([]));
    }
    if (canReadPmh) {
      loadSummaryMedicalHistory(patientId)
        .then((data) => !cancelled && setMedicalHistory(data))
        .catch(() => !cancelled && setMedicalHistory({ conditions: [], negatives: [] }));
    }
    if (canReadSurgHx) {
      loadSummarySurgicalHistory(patientId)
        .then((data) => !cancelled && setSurgicalHistory(data))
        .catch(() => !cancelled && setSurgicalHistory({ items: [] }));
    }
    if (canReadAllergies) {
      loadSummaryAllergies(patientId)
        .then((data) => !cancelled && setAllergies(data))
        .catch(() => !cancelled && setAllergies({ items: [], noKnownAllergies: false }));
    }
    return () => {
      cancelled = true;
    };
  }, [patientId, canReadProblems, canReadPmh, canReadSurgHx, canReadAllergies]);

  const openTab = (tabId) => {
    if (!chartTabs.some((tab) => tab.id === tabId)) return;
    setActiveTab(tabId);
  };
  const closeToSummary = () => {
    setActiveTab(firstAllowedTab(CLINICAL_TABS, "clinical", subtabAccess, "summary") || "summary");
  };
  const cyclePrevious = () => setActiveTab(cycleVisibleTab(clinicalTabs, activeTab, -1));
  const cycleNext = () => setActiveTab(cycleVisibleTab(clinicalTabs, activeTab, 1));

  const openEncounter = (enc, noteId) => {
    const path = encounterScreenPath(enc, noteId ? { noteId } : {});
    if (path) navigate(path);
  };

  return (
    <div className="emr-chart" data-testid={ENC.screenChartPanel}>
      <div className="chart-folder">
        <div className="emr-encdetail__chart-tabs-row">
          <ChartSubtabs
            tabs={chartTabs}
            activeId={activeTab}
            onSelect={openTab}
            icons={CHART_ICONS}
            testId={ENC.screenChartTabs}
            tabTestIdPrefix={ENC.screenChartTab}
            ariaLabel="Patient chart sections"
            listClass="chart-subtabs--clinical"
          />
          {onCollapse ? (
            <button
              type="button"
              data-testid={ENC.screenChartCollapse}
              onClick={onCollapse}
              className="emr-encdetail__chart-collapse-btn"
              aria-label="Collapse Patient Chart"
              title="Collapse Patient Chart"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="emr-chart-workspace min-h-0 flex-1 overflow-y-auto p-3">
          {activeTab === "summary" && (
            <ClinicalSummary
              fields={fields || {}}
              problemList={problemList}
              medicalHistory={medicalHistory}
              surgicalHistory={surgicalHistory}
              allergies={allergies}
              encounters={encounters}
              onOpenEncounters={canReadClinicalEncounters ? () => openTab("encounters") : undefined}
              onOpenResults={canReadResults ? () => openTab("results") : undefined}
              onOpenMedicalHistory={canReadPmh ? () => openTab("medical-history") : undefined}
              onOpenSurgicalHistory={canReadSurgHx ? () => openTab("surgical-history") : undefined}
              onOpenAllergies={canReadAllergies ? () => openTab("allergies") : undefined}
              onOpenProblemList={canReadProblems ? () => openTab("problem-list") : undefined}
              onSelectEncounter={openEncounter}
            />
          )}

          {activeTab === "problem-list" && (
            <ProblemListWorkspace patientId={patientId} onChange={setProblemList} accessReadOnly={!canWriteProblems} />
          )}

          {activeTab === "medical-history" && (
            <MedicalHistoryWorkspace
              patientId={patientId}
              birthDate={fields?.birthDate}
              onClose={closeToSummary}
              onPrevious={cyclePrevious}
              onNext={cycleNext}
              accessReadOnly={!canWritePmh}
            />
          )}

          {activeTab === "surgical-history" && (
            <SurgicalHistoryWorkspace
              patientId={patientId}
              patient={fields}
              currentUser={currentUser}
              onClose={closeToSummary}
              onPrevious={cyclePrevious}
              onNext={cycleNext}
              onChange={setSurgicalHistory}
              accessReadOnly={!canWriteSurgHx}
            />
          )}

          {activeTab === "encounters" && (
            <EncountersWorkspace encounters={encounters} onSelect={openEncounter} />
          )}

          {activeTab === "appointments" && (
            <AppointmentsWorkspace
              patientId={patientId}
              patientName={fields?.name}
              encounters={encounters}
              accessReadOnly={!canWriteClinAppointments}
              canWriteEncounters={canWriteEncounters}
              canAccessEncounters={canReadEncounters}
            />
          )}

          {activeTab === "notes" && (
            <NotesWorkspace
              patientId={patientId}
              encounters={encounters}
              currentUser={currentUser}
              onOpenNote={(note) => openEncounter({ id: note.encounterId }, note.id)}
              accessReadOnly={!canWriteNotes}
            />
          )}

          {activeTab === "results" && <ResultsPanel />}

          {activeTab === "medications" && (
            <Card title="Medications (Active)">
              <p className="emr-encdetail__hint">No patient-specific medications are available yet.</p>
            </Card>
          )}

          {activeTab === "allergies" && (
            <AllergiesWorkspace
              patientId={patientId}
              patient={fields}
              currentUser={currentUser}
              onClose={closeToSummary}
              onChange={setAllergies}
              accessReadOnly={!canWriteAllergies}
            />
          )}

          {activeTab === "miscellaneous" && (
            <Card title="Documents">
              <p className="emr-encdetail__hint">Patient documents are not implemented yet.</p>
            </Card>
          )}

          {activeTab === "orders" && <WhatToOrderPanel />}
        </div>
      </div>
    </div>
  );
}
